import React, { useEffect, useRef, useState } from 'react';
import { Check, X, Mail, PhoneCall } from 'lucide-react';
import {
  copyTextToClipboard,
  loadOrganizationContact,
  openSupportMail,
} from '../lib/organizationContact';

export default function BookingConfirmationModal({ open, requestCode, onClose, onViewProcess }) {
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [phoneCopied, setPhoneCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPhoneCopied(false);
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
      return undefined;
    }

    let isMounted = true;
    loadOrganizationContact().then((nextContact) => {
      if (isMounted) setContact(nextContact);
    });

    return () => {
      isMounted = false;
    };
  }, [open]);

  useEffect(() => () => {
    if (copiedTimerRef.current) {
      window.clearTimeout(copiedTimerRef.current);
    }
  }, []);

  if (!open) return null;

  const canEmail = Boolean(contact.email);
  const canCall = Boolean(contact.phone);

  const handleEmail = () => {
    openSupportMail({ email: contact.email, requestCode });
  };

  const handleCopyPhone = async () => {
    const copied = await copyTextToClipboard(contact.phone);
    if (!copied) return;
    setPhoneCopied(true);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => {
      setPhoneCopied(false);
      copiedTimerRef.current = null;
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
      <div className="relative w-full max-w-sm rounded-3xl bg-[#0c0d12] px-6 py-7 text-white shadow-2xl border border-white/20">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close confirmation"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white"
        >
          <X size={22} />
        </button>

        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#b10808] text-white shadow-[0_0_20px_rgba(177,8,8,0.35)]">
          <Check size={34} strokeWidth={3.25} />
        </div>

        <h3 className="text-center text-[31px] font-black leading-none tracking-tight text-white">Your booking has been submitted!</h3>
        <p className="mt-3 text-center text-xs leading-relaxed text-white/60">
          Thank you! We have received your request and our
          <br />
          team will review it shortly.
        </p>

        {requestCode ? (
          <p className="mt-2 text-center text-[11px] font-medium tracking-wide text-white/70">Request code: {requestCode}</p>
        ) : null}

        <div className="mt-6 rounded-xl border border-white/25 bg-[#13151d] px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col items-center gap-1 w-1/3">
              <span className="h-5 w-5 rounded-full bg-[#b10808] text-white text-[10px] font-bold flex items-center justify-center">✓</span>
              <p className="text-[10px] font-semibold text-white">Submitted</p>
              <p className="text-[9px] text-white/55">Just now</p>
            </div>

            <div className="h-[2px] flex-1 bg-[#b10808]" />

            <div className="flex flex-col items-center gap-1 w-1/3">
              <span className="h-5 w-5 rounded-full bg-[#262a34] border border-white/15 text-white/60 text-[10px] font-bold flex items-center justify-center">2</span>
              <p className="text-[10px] font-semibold text-white/80">Reviewing</p>
              <p className="text-[9px] text-white/45">Pending</p>
            </div>

            <div className="h-[2px] flex-1 bg-white/15" />

            <div className="flex flex-col items-center gap-1 w-1/3">
              <span className="h-5 w-5 rounded-full bg-[#262a34] border border-white/15 text-white/60 text-[10px] font-bold flex items-center justify-center">3</span>
              <p className="text-[10px] font-semibold text-white/80">Accepted</p>
              <p className="text-[9px] text-white/45">Pending</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onViewProcess || onClose}
          className="mt-5 w-full rounded-full bg-white text-[#b10808] py-3 text-sm font-black tracking-wide hover:bg-white/90"
        >
          View Booking Process
        </button>

        <p className="mt-4 text-center text-[11px] text-white/50">Need help? Contact Us</p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleEmail}
            disabled={!canEmail}
            title={canEmail ? `Email ${contact.email}` : 'No support email is set'}
            className="flex items-center justify-center gap-2 rounded-lg border border-white/40 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Mail size={14} className="text-[#ff2f3d]" />
            Email Us
          </button>

          <button
            type="button"
            onClick={handleCopyPhone}
            disabled={!canCall}
            title={canCall ? `Copy ${contact.phone}` : 'No support phone is set'}
            className="flex items-center justify-center gap-2 rounded-lg border border-white/40 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PhoneCall size={14} className="text-[#ff2f3d]" />
            {phoneCopied ? 'Copied' : 'Call Us'}
          </button>
        </div>
      </div>
    </div>
  );
}
